import { Collapse, FormControl, FormHelperText, MenuItem, Select } from '@material-ui/core';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import en from '../../../../lang/en-us';
import { CustomRadio } from '../../../../lib/CustomRadio';
import DatePickerInput from '../../../../lib/DatePicker';
import TextInput from '../../../../lib/TextField';
import {
    checkForOnlyAlphabets,
    isAlphaNumeric,
    _minDateForCalender,
} from '../../../../utils/constants';
import { NOMINEEINFO } from './update-nominee-edit';
import { IdType } from './schema';
import { CheckBoxField } from '../../../../lib/CheckBoxField';
import { useDispatch } from 'react-redux';
import { getCountries } from '../../../../redux-store/actions/link';

const { dashboard } = en;

type Countries = {
    name: string;
    dial_code: string;
    code: string;
};

export interface OptionalDetailsAccordionRef {
    openAccordion: () => void;
    scrollToNomineeField: (nomineeIndex: number, fieldName: string) => void;
}

interface OptionalDetailsAccordionProps {
    nominees: NOMINEEINFO[];
    handleNomineeChange: (key: string, value: any, index: number) => void;
    primaryHolderAddress?: any;
    fieldErrors?: { [key: string]: string }; // Map of "nomineeIndex-fieldName" to error message
}

/**
 * Optional Details Accordion Component
 * Shows all nominees' optional fields (DOB, Gender, Address, Email, Mobile, ID)
 * in a shared collapsible accordion section
 */
const OptionalDetailsAccordion = forwardRef<OptionalDetailsAccordionRef, OptionalDetailsAccordionProps>(
    ({ nominees, handleNomineeChange, primaryHolderAddress, fieldErrors = {} }, ref) => {
        const [isOpen, setIsOpen] = useState(false);
        const [countries, setCountries] = useState<Countries[]>([]);
        const dispatch = useDispatch();

        useEffect(() => {
            async function fetchCountries() {
                try {
                    const {
                        body: { countries },
                    } = await getCountries({})(dispatch);
                    setCountries(countries);
                } catch (e) {
                    // Handle error silently
                }
            }
            fetchCountries();
        }, []);

        const toggleAccordion = () => {
            setIsOpen(!isOpen);
        };

        // Expose methods to parent via ref
        useImperativeHandle(ref, () => ({
            openAccordion: () => {
                setIsOpen(true);
            },
            scrollToNomineeField: (nomineeIndex: number, fieldName: string) => {
                if (nomineeIndex === null || nomineeIndex === undefined || nomineeIndex < 0) return;
                if (!fieldName) return;

                setIsOpen(true);
                setTimeout(() => {
                    try {
                        const fieldElement = document.querySelector(
                            `[data-nominee-index="${nomineeIndex}"][data-field-name="${fieldName}"]`
                        );
                        if (fieldElement) {
                            const elementRect = fieldElement.getBoundingClientRect();
                            const absoluteElementTop = elementRect.top + window.pageYOffset;
                            const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
                            
                            if ('scrollBehavior' in document.documentElement.style) {
                                window.scrollTo({
                                    top: middle,
                                    behavior: 'smooth'
                                });
                            } else {
                                const startPosition = window.pageYOffset;
                                const distance = middle - startPosition;
                                const duration = 500;
                                let startTime: number | null = null;

                                const animation = (currentTime: number) => {
                                    if (startTime === null) startTime = currentTime;
                                    const timeElapsed = currentTime - startTime;
                                    const progress = Math.min(timeElapsed / duration, 1);
                                    const ease = progress < 0.5 
                                        ? 2 * progress * progress 
                                        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                                    window.scrollTo(0, startPosition + distance * ease);
                                    if (timeElapsed < duration) {
                                        requestAnimationFrame(animation);
                                    }
                                };
                                requestAnimationFrame(animation);
                            }
                            
                            setTimeout(() => {
                                const inputElement = fieldElement.querySelector('input, select');
                                if (inputElement) {
                                    (inputElement as HTMLElement).focus();
                                }
                            }, 550);
                        }
                    } catch (e) {
                        // Silently fail if DOM element not found
                    }
                }, 300);
            },
        }));

        return (
            <div className="optional-details-accordion-container">
                {/* Accordion Header */}
                <div
                    className={`optional-details-accordion-header ${isOpen ? 'open' : ''}`}
                    onClick={toggleAccordion}
                >
                    <div className="accordion-icon-circle">{isOpen ? '−' : '+'}</div>
                    <div className="accordion-header-text">
                        <span className="accordion-title-text">
                            {dashboard.UpdateNominee.optionalDetailsAccordion.title}
                        </span>
                        <span className="accordion-subtitle-text">
                            {dashboard.UpdateNominee.optionalDetailsAccordion.subtitle}
                        </span>
                    </div>
                    <div className={`accordion-arrow-icon ${isOpen ? 'open' : ''}`}>▼</div>
                </div>

                {/* Accordion Content */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <div className="optional-details-accordion-content">
                        {nominees.map((nominee, index) => (
                            <NomineeOptionalFields
                                key={nominee.id || index}
                                index={index}
                                nominee={nominee}
                                handleNomineeChange={handleNomineeChange}
                                primaryHolderAddress={primaryHolderAddress}
                                countries={countries}
                                fieldErrors={fieldErrors}
                            />
                        ))}
                    </div>
                </Collapse>
            </div>
        );
    }
);

interface NomineeOptionalFieldsProps {
    index: number;
    nominee: NOMINEEINFO;
    handleNomineeChange: (key: string, value: any, index: number) => void;
    primaryHolderAddress?: any;
    countries: Countries[];
    fieldErrors: { [key: string]: string };
}

// Validation regex patterns
const emailRegex = /^[\w.!#$%&'*+/=?^_`{|}~-]+@[\w-]+(\.[\w-]+)+$/;
const mobileRegex = /^[6-9]\d{9}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const indiaPincodeRegex = /^\d{6}$/;
const nonIndiaPincodeRegex = /^[A-Za-z0-9]{4,20}$/;
const drivingLicenseRegex = /^[A-Z]{2}[A-Za-z0-9_]{8,48}$/;
const aadharRegex = /^\d{4}$/;
const passportNumberRegex = /(^[A-PR-WY][0-9]{7}$)|(^[A-Z0-9]{5,15}$)/;

function NomineeOptionalFields({
    index,
    nominee,
    handleNomineeChange,
    primaryHolderAddress,
    countries,
    fieldErrors,
}: NomineeOptionalFieldsProps) {
    // Local validation errors for real-time feedback
    const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});
    
    const handleChange = (key: string, value: any) => {
        handleNomineeChange(key, value, index);
        // Clear local error when user starts editing
        if (localErrors[key]) {
            setLocalErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    // Validate a field on blur and set local error
    const validateField = (fieldName: string, value: any) => {
        let error = '';
        
        switch (fieldName) {
            case 'email':
                if (value && value.trim() !== '' && !emailRegex.test(value)) {
                    error = 'Please enter a valid email';
                }
                break;
            case 'mobile':
                if (value && value.trim() !== '' && !mobileRegex.test(value)) {
                    error = 'Please enter a valid 10-digit mobile number';
                }
                break;
            case 'pincode':
                if (value && String(value).trim() !== '') {
                    const pincodeStr = String(value).trim();
                    const isIndia = !nominee.country || nominee.country === 'India' || nominee.country === '';
                    if (isIndia) {
                        if (!indiaPincodeRegex.test(pincodeStr)) {
                            error = 'Please enter a valid 6-digit pincode';
                        }
                    } else {
                        if (!nonIndiaPincodeRegex.test(pincodeStr)) {
                            error = 'Please enter a valid pincode';
                        }
                    }
                }
                break;
            case 'docValue':
                if (nominee.docName && value && value.trim() !== '') {
                    const docValue = value.trim();
                    if (nominee.docName === 'A') {
                        if (!aadharRegex.test(docValue)) {
                            error = 'Please enter last 4 digits of Aadhaar';
                        } else if (docValue === '0000') {
                            error = 'Please enter a valid Aadhaar number';
                        }
                    } else if (nominee.docName === 'P') {
                        if (!panRegex.test(docValue)) {
                            error = 'Please enter a valid PAN';
                        }
                    } else if (nominee.docName === 'D') {
                        if (!drivingLicenseRegex.test(docValue)) {
                            error = 'Please enter a valid driving license';
                        }
                    } else if (nominee.docName === 'I') {
                        if (!passportNumberRegex.test(docValue)) {
                            error = 'Please enter a valid passport number';
                        }
                    }
                }
                break;
            case 'guardianName':
                if (nominee.isMinor && value && value.trim().length > 0 && value.trim().length < 4) {
                    error = 'Guardian name must be at least 4 characters';
                }
                break;
            case 'guardianPan':
                if (nominee.isMinor && value && value.trim() !== '' && !panRegex.test(value)) {
                    error = 'Please enter a valid PAN';
                }
                break;
        }
        
        setLocalErrors(prev => {
            if (error) {
                return { ...prev, [fieldName]: error };
            } else {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            }
        });
    };

    // Helper to get error for a specific field (combine local and prop errors)
    const getFieldError = (fieldName: string): string | undefined => {
        return localErrors[fieldName] || fieldErrors[`${index}-${fieldName}`];
    };

    const isMinor = nominee.isMinor || false;

    return (
        <div className="nominee-optional-section" data-nominee-section={index}>
            {/* Nominee Header */}
            <div className="nominee-optional-header">
                <div className="nominee-badge-small">{index + 1}</div>
                <span className="nominee-label">Nominee {index + 1}</span>
            </div>

            {/* Date of Birth */}
            {!isMinor && (
            <div className="optional-field-row" data-nominee-index={index} data-field-name="dob">
                <div className="optional-field optional-field-half">
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.dob}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <DatePickerInput
                        id={`nominee-dob-opt-${index}`}
                        value={nominee.dob || null}
                        onChange={(date: any) => handleChange('dob', date)}
                        minDate={_minDateForCalender()}
                        invalidDateMessage=""
                    />
                </div>
            </div>
            )}

            {/* Gender */}
            <div className="optional-field-row" data-nominee-index={index} data-field-name="gender">
                <div className="optional-field optional-field-full">
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.gender}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <CustomRadio
                        name={`gender-${index}`}
                        selectedValue={nominee.gender}
                        handleRadioChange={(value: any) => handleChange('gender', value)}
                        list={[
                            { label: 'Male', value: 'male' },
                            { label: 'Female', value: 'female' },
                            { label: 'Others', value: 'others' },
                        ]}
                    />
                </div>
            </div>

            {/* Guardian Details - Show when minor is checked */}
            {isMinor && (
                <div className="guardian-section">
                    <h4 className="guardian-heading">
                        {dashboard.UpdateNominee.guardianDetailsHeading}
                    </h4>
                    <div className="optional-field-row">
                        <div
                            className="optional-field"
                            data-nominee-index={index}
                            data-field-name="guardianName"
                        >
                            <label className="optional-field-label">
                                Guardian Name{' '}
                                <span className="optional-tag">{dashboard?.UpdateNominee?.optional}</span>
                            </label>
                            <TextInput
                                variant="outlined"
                                placeholder="Guardian full name"
                                value={nominee.guardianName || ''}
                                name="guardianName"
                                onChange={(e: any) => {
                                    if (checkForOnlyAlphabets(e.target.value) || !e.target.value) {
                                        handleChange('guardianName', e.target.value);
                                    }
                                }}
                                error={!!getFieldError('guardianName')}
                                helperText={getFieldError('guardianName')}
                            />
                        </div>
                        <div
                            className="optional-field"
                            data-nominee-index={index}
                            data-field-name="guardianPan"
                        >
                            <label className="optional-field-label">
                                Guardian PAN{' '}
                                <span className="optional-tag">{dashboard?.UpdateNominee?.optional}</span>
                            </label>
                            <TextInput
                                variant="outlined"
                                placeholder="Enter PAN"
                                value={nominee.guardianPan || ''}
                                name="guardianPan"
                                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                                onChange={(e: any) => {
                                    const val = e.target.value.toUpperCase();
                                    if (/^[A-Z0-9]*$/.test(val) || !val) {
                                        handleChange('guardianPan', val);
                                    }
                                }}
                                error={!!getFieldError('guardianPan')}
                                helperText={getFieldError('guardianPan')}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Address same as Primary Holder */}
            <div className="address-same-section">
                <CheckBoxField
                    label={dashboard.UpdateNominee.nomineeFields.addressSameAsFH}
                    value={nominee.addressSameAsFH === 'Y'}
                    onChange={(e: any) => {
                        const checked = e.target.checked;
                        handleChange('addressSameAsFH', checked ? 'Y' : 'N');
                        if (checked && primaryHolderAddress) {
                            handleChange('address1', primaryHolderAddress.line1 || '');
                            handleChange('address2', primaryHolderAddress.line2 || '');
                            handleChange('country', primaryHolderAddress.country || '');
                            handleChange('city', primaryHolderAddress.city || '');
                            handleChange('state', primaryHolderAddress.state || '');
                            handleChange('pincode', primaryHolderAddress.pincode || '');
                        } else if (!checked) {
                            handleChange('address1', '');
                            handleChange('address2', '');
                            handleChange('country', '');
                            handleChange('city', '');
                            handleChange('state', '');
                            handleChange('pincode', '');
                        }
                    }}
                />
            </div>

            {/* Address Fields */}
            <div className="optional-field-row">
                <div
                    className="optional-field"
                    data-nominee-index={index}
                    data-field-name="address1"
                >
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.address1}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.nomineeFields.address1}
                        value={nominee.address1}
                        name="address1"
                        onChange={(e: any) => {
                            handleChange('address1', e.target.value);
                        }}
                        error={!!getFieldError('address1')}
                        helperText={getFieldError('address1')}
                    />
                </div>
                <div
                    className="optional-field"
                    data-nominee-index={index}
                    data-field-name="address2"
                >
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.address2}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.nomineeFields.address2}
                        value={nominee.address2}
                        name="address2"
                        onChange={(e: any) => {
                            handleChange('address2', e.target.value);
                        }}
                        error={!!getFieldError('address2')}
                        helperText={getFieldError('address2')}
                    />
                </div>
            </div>

            <div className="optional-field-row">
                <div
                    className="optional-field"
                    data-nominee-index={index}
                    data-field-name="country"
                >
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.country}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <FormControl variant="outlined" fullWidth error={!!getFieldError('country')}>
                        <Select
                            value={
                                nominee.country || dashboard.UpdateNominee.nomineeFields.country
                            }
                            onChange={(e) => {
                                handleChange('country', e.target.value);
                                handleChange('pincode', '');
                            }}
                        >
                            <MenuItem value={dashboard.UpdateNominee.nomineeFields.country}>
                                {dashboard.UpdateNominee.nomineeFields.country}
                            </MenuItem>
                            {countries.map((country) => (
                                <MenuItem value={country.name} key={country.code}>
                                    {country.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {getFieldError('country') && (
                            <FormHelperText>{getFieldError('country')}</FormHelperText>
                        )}
                    </FormControl>
                </div>
                <div className="optional-field" data-nominee-index={index} data-field-name="city">
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.city}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.nomineeFields.city}
                        value={nominee.city}
                        name="city"
                        onChange={(e: any) => {
                            if (checkForOnlyAlphabets(e.target.value) || !e.target.value) {
                                handleChange('city', e.target.value);
                            }
                        }}
                        error={!!getFieldError('city')}
                        helperText={getFieldError('city')}
                    />
                </div>
            </div>

            <div className="optional-field-row">
                <div className="optional-field" data-nominee-index={index} data-field-name="state">
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.state}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.nomineeFields.state}
                        value={nominee.state}
                        name="state"
                        onChange={(e: any) => {
                            if (
                                (checkForOnlyAlphabets(e.target.value) || !e.target.value) &&
                                e.target.value.length <= 20
                            ) {
                                handleChange('state', e.target.value);
                            }
                        }}
                        error={!!getFieldError('state')}
                        helperText={getFieldError('state')}
                    />
                </div>
                <div
                    className="optional-field"
                    data-nominee-index={index}
                    data-field-name="pincode"
                >
                    <label className="optional-field-label">
                        PIN Code{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder="PIN Code"
                        value={nominee.pincode}
                        name="pincode"
                        onChange={(e: any) => {
                            const nonIndiaPincodeRegex = /^[A-Za-z0-9]+$/;
                            const indiaPincodeRegex = /^[0-9]+$/;
                            const regex =
                                nominee.country === 'India'
                                    ? indiaPincodeRegex
                                    : nonIndiaPincodeRegex;
                            const maxLength = nominee.country === 'India' ? 6 : 20;
                            if (
                                (regex.test(e.target.value) || !e.target.value) &&
                                e.target.value.length <= maxLength
                            ) {
                                handleChange('pincode', e.target.value.toUpperCase());
                            }
                        }}
                        error={!!getFieldError('pincode')}
                        helperText={getFieldError('pincode')}
                    />
                </div>
            </div>

            {/* Email and Mobile */}
            <div className="optional-field-row">
                <div className="optional-field" data-nominee-index={index} data-field-name="email">
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.email}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.nomineeFields.email}
                        value={nominee.email}
                        name="email"
                        onChange={(e: any) => handleChange('email', e.target.value)}
                        error={!!getFieldError('email')}
                        helperText={getFieldError('email')}
                    />
                </div>
                <div className="optional-field" data-nominee-index={index} data-field-name="mobile">
                    <label className="optional-field-label">
                        {dashboard.UpdateNominee.nomineeFields.mobile}{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.nomineeFields.mobile}
                        value={nominee.mobile}
                        name="mobile"
                        onChange={(e: any) => handleChange('mobile', e.target.value)}
                        error={!!getFieldError('mobile')}
                        helperText={getFieldError('mobile')}
                    />
                </div>
            </div>

            {/* ID Type and ID Number */}
            <div className="optional-field-row">
                <div
                    className="optional-field"
                    data-nominee-index={index}
                    data-field-name="docName"
                >
                    <label className="optional-field-label">
                        ID Type{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <FormControl variant="outlined" fullWidth error={!!getFieldError('docName')}>
                        <Select
                            value={
                                nominee.docName || dashboard.UpdateNominee.nomineeFields.docName
                            }
                            onChange={(e) => {
                                handleChange('docName', e.target.value);
                                handleChange('docValue', '');
                            }}
                        >
                            <MenuItem value={dashboard.UpdateNominee.nomineeFields.docName}>
                                ID Type
                            </MenuItem>
                            <MenuItem value={IdType.Aadhaar}>Aadhaar</MenuItem>
                            <MenuItem value={IdType.PAN}>PAN</MenuItem>
                            <MenuItem value={IdType.DrivingLicense}>Driving License</MenuItem>
                            <MenuItem value={IdType.PassportNumber}>Passport Number</MenuItem>
                        </Select>
                        {getFieldError('docName') && (
                            <FormHelperText>{getFieldError('docName')}</FormHelperText>
                        )}
                    </FormControl>
                </div>
                <div
                    className="optional-field"
                    data-nominee-index={index}
                    data-field-name="docValue"
                >
                    <label className="optional-field-label">
                        ID Number{' '}
                        <span className="optional-tag">{dashboard.UpdateNominee.optional}</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder="ID Number"
                        value={nominee.docValue}
                        name="docValue"
                        onChange={(e: any) =>
                            handleChange('docValue', e.target.value.toUpperCase())
                        }
                        error={!!getFieldError('docValue')}
                        helperText={getFieldError('docValue')}
                    />
                </div>
            </div>
        </div>
    );
}

export default OptionalDetailsAccordion;
